import { Dispatch, memo, useCallback, useEffect, useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';
import { SetStateAction } from 'jotai';
import { useTranslation } from 'next-i18next';
import { useForm } from 'react-hook-form';
import { useAccountsService } from '@restworld/data-services';
import { Button, Modal, ModalContent } from '@restworld/ui-ds-base';

import { useEnterEmailContent } from './enter-email-content';
import { EditEmailModalMode, useEditEmailForm } from '../hooks/use-edit-email-form';
import { useEmailSubmittedContent } from './email-submitted-content';
import { TokenVerificationProgressingContent } from './token-verification-progressing-content';
import { TokenVerificationSuccessfulContent } from './token-verification-successful-content';
import { TokenVerificationFailedContent } from './token-verification-failed-content';
import { EmailLoginInput, useVerifyEmailForm } from '../hooks/use-verify-email-form';

export type EditEmailModalProps = {
  email?: string;
};

export const EditEmailModal = memo(({ email }: EditEmailModalProps) => {
  const { onOpen, onClose, open, mode, setMode, submittedEmail, setSubmittedEmail } = useEditEmailForm();
  const { t } = useTranslation('user-settings');

  const acc = useAccountsService();
  const router = useRouter();
  const token = router?.query?.token;

  const handleVerifyToken = useCallback(
    (token: string) => {
      acc
        .verifyEmail({ token: token })
        .then((res: AxiosResponse) => {
          router.push('/impostazioni');
          setMode(EditEmailModalMode.TokenVerificationSuccess);
        })
        .catch((err) => {
          router.push('/impostazioni');
          setMode(EditEmailModalMode.TokenVerificationError);
        });
    },
    [acc, router, setMode]
  );

  useEffect(() => {
    if (token) {
      onOpen();
      setMode(EditEmailModalMode.TokenVerificationProgressing);
      handleVerifyToken(token as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <>
      <Button variant="tertiary" size="sm" className="whitespace-nowrap" onClick={onOpen}>
        {t('edit_email_action_text')}
      </Button>
      {open && <EditContentModal {...{ mode, setMode, submittedEmail, setSubmittedEmail, email, onClose, open }} />}
    </>
  );
});

const EditContentModal = ({
  mode,
  setMode,
  submittedEmail,
  setSubmittedEmail,
  email,
  onClose,
  open,
}: {
  mode: EditEmailModalMode;
  setMode: Dispatch<SetStateAction<EditEmailModalMode>>;
  setSubmittedEmail: Dispatch<SetStateAction<string>>;
  submittedEmail: string;
  email?: string;
  onClose: () => void;
  open: boolean;
}) => {
  const methods = useForm<EmailLoginInput>({
    defaultValues: {
      email,
    },
  });
  const { handleSubmit } = methods;

  const { onSubmit, loading, remainingTimes } = useVerifyEmailForm({
    setMode,
    setSubmittedEmail,
    email,
    methods,
  });

  const onFormSubmit = handleSubmit(onSubmit);

  const { enterEmailContent, enterEmailFooter } = useEnterEmailContent({
    ...{ loading, methods, onFormSubmit },
  });

  const { emailSubmittedContent, emailSubmittedFooter } = useEmailSubmittedContent({
    ...{ email: submittedEmail, setMode, setSubmittedEmail, loading, remainingTimes, onFormSubmit },
  });
  const { t: commonT } = useTranslation('common');

  const renderFooter = useMemo(
    () =>
      mode === EditEmailModalMode.TokenVerificationSuccess || mode === EditEmailModalMode.TokenVerificationError ? (
        <Button fullwidth={true} onClick={onClose}>
          {commonT('close')}
        </Button>
      ) : mode === EditEmailModalMode.EnterEmail ? (
        enterEmailFooter
      ) : mode === EditEmailModalMode.EmailSubmitted ? (
        emailSubmittedFooter
      ) : null,
    [commonT, emailSubmittedFooter, enterEmailFooter, mode, onClose]
  );

  return (
    <Modal open={open} onOpenChange={(v) => !v && onClose()}>
      <ModalContent
        variant="center"
        showBackButton={false}
        title=""
        className="lg:min-w-[31.75rem] lg:max-w-[31.75rem]"
        footer={renderFooter}
      >
        <form className="w-full pt-5" onSubmit={handleSubmit(onSubmit)}>
          {mode === EditEmailModalMode.EnterEmail && enterEmailContent}
          {mode === EditEmailModalMode.EmailSubmitted && emailSubmittedContent}
          {mode === EditEmailModalMode.TokenVerificationProgressing && <TokenVerificationProgressingContent />}
          {mode === EditEmailModalMode.TokenVerificationSuccess && <TokenVerificationSuccessfulContent />}
          {mode === EditEmailModalMode.TokenVerificationError && <TokenVerificationFailedContent />}
        </form>
      </ModalContent>
    </Modal>
  );
};
